import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { Kpi } from '@/types/dashboards';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

function formatValue(value: Kpi['value'], unit: Kpi['unit']): string {
  if (value === null) return '—';
  if (typeof value === 'string') return value;
  return unit ? `${NUM.format(value)} ${unit}` : NUM.format(value);
}

export interface WeekendAfterHoursCardProps {
  kpis: Kpi[] | undefined;
  isLoading: boolean;
  error: Error | null;
  emptyMessage?: string;
  className?: string;
}

/**
 * Weekend / After-Hours Spend card — `GET /api/v1/dashboards/weekend-hours`.
 * Two KPIs (`weekend_spend_ratio`, `after_hours_spend_share`), both day-level
 * aggregates that are robust to the known midnight-timestamp ingest bug
 * (the endpoint excludes synthetic-midnight rows from the after-hours
 * denominator — see the KPI's own `story`). Rendered as a single card with
 * two side-by-side stats rather than a card grid, since the two numbers
 * are read together ("how much of spend falls outside a normal workday").
 */
export function WeekendAfterHoursCard({
  kpis,
  isLoading,
  error,
  emptyMessage = 'No weekend/after-hours spend data for this period.',
  className,
}: WeekendAfterHoursCardProps): JSX.Element {
  if (error) {
    return (
      <Card className={className ?? 'border-red-200 bg-red-50'}>
        <CardContent className="py-3 text-sm text-red-800">
          Could not load weekend/after-hours spend: {error.message}
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!kpis || kpis.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-3 text-sm text-muted-foreground">{emptyMessage}</CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Weekend / After-Hours Spend
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 pt-0 sm:grid-cols-2">
        {kpis.map((kpi) => (
          <div key={kpi.name} className="space-y-1">
            <div className="text-2xl font-semibold tabular-nums">
              {formatValue(kpi.value, kpi.unit)}
            </div>
            <p className="text-sm text-muted-foreground">{kpi.story}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default WeekendAfterHoursCard;
